
 
import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useSarvamLiveTranscript } from "./useSarvamLiveTranscript";

const USE_BROWSER_SPEECH_RECOGNITION = false;

const normalizeTranscriptText = (value = "") =>
  value.replace(/\s+/g, " ").trim();

// Browser TTS is played through the device speaker. On devices without strong
// echo cancellation, its prompt can be captured by the microphone. Remove only
// this known leading system prompt, never matching the candidate's answer.
const removeRecordingAnnouncement = (value = "") =>
  value
    .replace(
      /^\s*(?:recording\s+started[.!]?\s*)?(?:please\s+speak\s+now[.!]?\s*)?/i,
      "",
    )
    .trim();

const isSystemPromptEcho = (value = "") => {
  const normalized = normalizeTranscriptText(value).toLowerCase();
  return (
    /^(?:recording\s+started|please(?:\s+speak(?:\s+(?:now|up))?)?|speak\s+now)\b/.test(
      normalized,
    ) ||
    // Sarvam can return a one-word tail of the system announcement as a
    // partial (for example, "speaking"). It is never an answer by itself.
    /^(?:speak|speaking|now|please)$/.test(normalized)
  );
};

const removeRepeatedTranscriptText = (value = "") => {
  const words = normalizeTranscriptText(value).split(" ").filter(Boolean);
  const cleanedWords = [];

  for (const word of words) {
    const previousWord = cleanedWords[cleanedWords.length - 1];
    if (previousWord && previousWord.toLowerCase() === word.toLowerCase()) {
      continue;
    }

    cleanedWords.push(word);
  }

  for (
    let phraseLength = Math.min(6, Math.floor(cleanedWords.length / 2));
    phraseLength > 1;
    phraseLength -= 1
  ) {
    for (
      let index = phraseLength;
      index <= cleanedWords.length - phraseLength;
      index += 1
    ) {
      const previousPhrase = cleanedWords
        .slice(index - phraseLength, index)
        .join(" ")
        .toLowerCase();
      const nextPhrase = cleanedWords
        .slice(index, index + phraseLength)
        .join(" ")
        .toLowerCase();

      if (previousPhrase === nextPhrase) {
        cleanedWords.splice(index, phraseLength);
        index = Math.max(phraseLength - 1, index - phraseLength);
      }
    }
  }

  return cleanedWords.join(" ");
};

const mergeTranscriptText = (previousValue = "", nextValue = "") => {
  const previous = removeRepeatedTranscriptText(previousValue);
  const next = removeRepeatedTranscriptText(nextValue);

  if (!next) return previous;
  if (!previous) return next;

  const previousLower = previous.toLowerCase();
  const nextLower = next.toLowerCase();

  if (previousLower.endsWith(nextLower)) {
    return previous;
  }

  if (nextLower.startsWith(previousLower)) {
    return next;
  }

  const previousWords = previous.split(" ");
  const nextWords = next.split(" ");
  const maxOverlap = Math.min(previousWords.length, nextWords.length);

  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    const previousTail = previousWords
      .slice(previousWords.length - overlap)
      .join(" ")
      .toLowerCase();
    const nextHead = nextWords.slice(0, overlap).join(" ").toLowerCase();

    if (previousTail === nextHead) {
      return removeRepeatedTranscriptText(
        `${previous} ${nextWords.slice(overlap).join(" ")}`
      );
    }
  }

  return removeRepeatedTranscriptText(`${previous} ${next}`);
};
 
export const QuestionPopup = ({
  question,
  currentQuestion,
  isRecording,
  stream,
  audioStream,
  startRecording,
  handleSave,
  onClose,
  onSaveStart,
  onSaveEnd,
  transcript,
  setTranscript,
}) => {
  // ========== STATE ==========
  const [micLevel, setMicLevel] = useState(0);
  const [timer, setTimer] = useState(15);
  const [progress, setProgress] = useState(0);
  const [timerFinished, setTimerFinished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavingPopup, setShowSavingPopup] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState("");
  const [isTranscriptCaptureActive, setIsTranscriptCaptureActive] = useState(false);
  const [isQuestionReading, setIsQuestionReading] = useState(false);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [retrying, setRetrying] = useState(false);
 
  // ========== REFS ==========
  const countdownRef = useRef(null);
  const activeQuestionRef = useRef(0);
  // Kept for the retired browser-recognition retry/save paths below.
  // The Sarvam path is active through useSarvamLiveTranscript.
  const recognitionRef = useRef(null);
  const stoppedByUser = useRef(false);
  const isRecordingRef = useRef(false);
  const isQuestionSpokenRef = useRef(false);
  const isQuestionTriggeredRef = useRef(false);
  const countdownStartedRef = useRef(false);
  const questionTextRef = useRef('');
  const isRetrying = useRef(false);
  const recognitionRestartTimeout = useRef(null);
  const autoResumeTimeoutRef = useRef(null);
  const isRecognitionStartingRef = useRef(false);
  const lastFinalTranscriptRef = useRef("");
  const committedTranscriptRef = useRef("");
  const transcriptActivationTimeoutRef = useRef(null);

  useSarvamLiveTranscript({
    audioStream,
    enabled: timerFinished && isRecording && isTranscriptCaptureActive,
    questionText: question?.QuestionDescription,
    onTranscript: (text, isFinal) => {
      // Discard an entire recognition result that begins with the spoken UI
      // prompt (including distorted echoes such as "Please speak up").
      if (isSystemPromptEcho(text)) return;

      const nextText = removeRepeatedTranscriptText(
        removeRecordingAnnouncement(text),
      );
      if (!nextText) return;

      // Sarvam sends changing partials while the candidate speaks. Display the
      // current partial without committing it; commit only its final result so
      // words are never appended repeatedly on every partial event.
      const displayText = mergeTranscriptText(committedTranscriptRef.current, nextText);
      if (isFinal) {
        if (nextText === lastFinalTranscriptRef.current) return;
        lastFinalTranscriptRef.current = nextText;
        committedTranscriptRef.current = displayText;
      }
      setTranscriptionError("");
      setTranscript(displayText);
    },
    onError: (message) => {
      console.error("Sarvam live transcription:", message);
      setTranscriptionError(message || "Live transcription is unavailable");
    },
    onLanguageMismatch: (expectedLanguageName) => {
      setTranscriptionError(`Please answer in ${expectedLanguageName}.`);
    },
  });
 
  // ========== SPEECH RECOGNITION SETUP ==========
  useEffect(() => {
    if (!USE_BROWSER_SPEECH_RECOGNITION) return undefined;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech Recognition not supported");
      return;
    }
 
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
 
    // Fixed onresult handler - uses resultIndex and handles duplicates
    recognition.onresult = (event) => {
      // Clear any pending retry flag when we get results
      isRetrying.current = false;
      setRetrying(false);
     
      let text = "";
      // Process only new results using resultIndex
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          text += event.results[i][0].transcript + " ";
        }
      }
     
      // Only update if we have new text
      if (text.trim()) {
        const finalText = removeRepeatedTranscriptText(text);
        if (finalText === lastFinalTranscriptRef.current) {
          return;
        }
        lastFinalTranscriptRef.current = finalText;
        setTranscript((previousTranscript) => {
          return mergeTranscriptText(previousTranscript, finalText);
        });
        console.log("📝 Transcript updated:", text.trim());
      }
    };
 
    recognition.onerror = (event) => {
      if (event.error !== "no-speech" && event.error !== "aborted") {
        console.error("❌ Speech recognition error:", event.error);
      }
      // Reset retry flag on error
      isRetrying.current = false;
      setRetrying(false);
    };
 
    // Browser speech recognition can end after silence, especially on tablets.
    // Resume listening quietly without invoking Retry or clearing the transcript.
    recognition.onend = () => {
      if (
        !stoppedByUser.current &&
        !isRetrying.current &&
        timerFinished
      ) {
        if (autoResumeTimeoutRef.current) {
          return;
        }

        autoResumeTimeoutRef.current = setTimeout(() => {
          autoResumeTimeoutRef.current = null;

          if (
            stoppedByUser.current ||
            isRetrying.current
          ) {
            return;
          }

          try {
            isRecognitionStartingRef.current = true;
            recognition.start();
            console.log("Speech recognition resumed after pause.");
          } catch (err) {
            console.log("Recognition resume after pause failed:", err);
          } finally {
            isRecognitionStartingRef.current = false;
          }
        }, 300);
      }
    };
 
    recognitionRef.current = recognition;
 
    return () => {
      stoppedByUser.current = true;
      isRetrying.current = false;
      if (recognitionRestartTimeout.current) {
        clearTimeout(recognitionRestartTimeout.current);
      }
      if (transcriptActivationTimeoutRef.current) {
        clearTimeout(transcriptActivationTimeoutRef.current);
      }
      if (autoResumeTimeoutRef.current) {
        clearTimeout(autoResumeTimeoutRef.current);
        autoResumeTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          // Ignore stop errors on cleanup
        }
      }
    };
  }, [timerFinished]);
 
  // ========== SPEECH RECOGNITION CONTROL ==========
  useEffect(() => {
    if (!recognitionRef.current) return;
 
    if (timerFinished && stoppedByUser.current && !isRetrying.current) {
      stoppedByUser.current = false;
      isRetrying.current = false;
      setRetrying(false);
      try {
        isRecognitionStartingRef.current = true;
        recognitionRef.current.start();
        isRecognitionStartingRef.current = false;
        console.log("✅ Speech recognition started");
      } catch (err) {
        console.log("❌ Start error:", err);
      }
    } else if (!timerFinished && !isRecording) {
      stoppedByUser.current = true;
      isRetrying.current = false;
      setRetrying(false);
      try {
        recognitionRef.current.stop();
        console.log("⏹️ Speech recognition stopped");
      } catch (err) {
        console.log("❌ Stop error:", err);
      }
    }
  }, [timerFinished, isRecording]);
 
  // ========== UPDATE RECORDING REF ==========
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
 
  // ========== RESET ON QUESTION CHANGE ==========
  useEffect(() => {
    activeQuestionRef.current++;
   
    // Reset all refs
    isQuestionSpokenRef.current = false;
    isQuestionTriggeredRef.current = false;
    countdownStartedRef.current = false;
    questionTextRef.current = question?.QuestionDescription || '';
    isRetrying.current = false;
    setRetrying(false);
   
    // Clear any pending retry timeout
    if (recognitionRestartTimeout.current) {
      clearTimeout(recognitionRestartTimeout.current);
      recognitionRestartTimeout.current = null;
    }
    if (autoResumeTimeoutRef.current) {
      clearTimeout(autoResumeTimeoutRef.current);
      autoResumeTimeoutRef.current = null;
    }
    lastFinalTranscriptRef.current = "";
    committedTranscriptRef.current = "";
    setIsTranscriptCaptureActive(false);

    if (transcriptActivationTimeoutRef.current) {
      clearTimeout(transcriptActivationTimeoutRef.current);
      transcriptActivationTimeoutRef.current = null;
    }

    // Reset states
    setTimer(15);
    setProgress(0);
    setTimerFinished(false);
    setIsQuestionReading(false);
    setIsCountdownActive(false);
 
    // Clear previous countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
 
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
 
  }, [currentQuestion, question]);
 
  // ========== COUNTDOWN TIMER ==========
  const startCountdown = () => {
    if (countdownStartedRef.current) {
      return;
    }
 
    if (!isQuestionSpokenRef.current) {
      return;
    }
 
    countdownStartedRef.current = true;
    setIsCountdownActive(true);
    setIsQuestionReading(false);
    console.log("⏱️ Step 3: Countdown started (15 seconds)");
 
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
 
    setTimer(15);
    setProgress(0);
    setTimerFinished(false);
 
    countdownRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setTimerFinished(true);
          setIsCountdownActive(false);
          console.log("⏱️ Step 4: Countdown finished");
          console.log("🔴 Step 5: UI shows 'Recording started — speak now' with blinking red dot");
         
          speakRecordingStarted();
          return 0;
        }
        return t - 1;
      });
      setProgress((p) => Math.min(100, p + 100 / 15));
    }, 1000);
  };
 
  // ========== CLEANUP COUNTDOWN ON UNMOUNT ==========
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);
 
  // ========== HANDLE RETRY ==========
  const handleRetry = () => {
    if (!USE_BROWSER_SPEECH_RECOGNITION) {
      setTranscript("");
      lastFinalTranscriptRef.current = "";
      committedTranscriptRef.current = "";
      setRetrying(false);
      return;
    }
    // Prevent multiple retry attempts
    if (isRetrying.current) {
      console.log("⏭️ Retry already in progress, skipping");
      return;
    }
 
    if (!recognitionRef.current) {
      console.log("❌ No recognition instance available");
      return;
    }
 
    // Set retry flag
    isRetrying.current = true;
    setRetrying(true);
   
    // Clear any existing transcript
    setTranscript("");
    lastFinalTranscriptRef.current = "";
   
    // Stop current recognition
    stoppedByUser.current = true;
    try {
      recognitionRef.current.stop();
      console.log("⏹️ Recognition stopped for retry");
    } catch (err) {
      console.log("❌ Error stopping recognition:", err);
      isRetrying.current = false;
      setRetrying(false);
      return;
    }
 
    // Clear any pending restart timeout
    if (recognitionRestartTimeout.current) {
      clearTimeout(recognitionRestartTimeout.current);
      recognitionRestartTimeout.current = null;
    }
    if (autoResumeTimeoutRef.current) {
      clearTimeout(autoResumeTimeoutRef.current);
      autoResumeTimeoutRef.current = null;
    }
 
    // Wait for complete stop and then restart
    recognitionRestartTimeout.current = setTimeout(() => {
      if (!recognitionRef.current) {
        isRetrying.current = false;
        setRetrying(false);
        return;
      }
 
      // Reset flags
      stoppedByUser.current = false;
     
      try {
        recognitionRef.current.start();
        console.log("🔄 Retry - recognition restarted successfully");
        // Reset retry flag after successful start
        setTimeout(() => {
          isRetrying.current = false;
          setRetrying(false);
        }, 500);
      } catch (err) {
        console.log("❌ Error starting recognition:", err);
        isRetrying.current = false;
        setRetrying(false);
      }
     
      recognitionRestartTimeout.current = null;
    }, 500);
  };
 
  // ========== TEXT-TO-SPEECH: Question ==========
  const speakQuestion = (text) => {
    if (isQuestionSpokenRef.current) {
      console.log("⏭️ Question already spoken, skipping");
      startCountdown();
      return;
    }
 
    if (isQuestionTriggeredRef.current) {
      console.log("⏭️ Question already triggered, skipping");
      return;
    }
 
    isQuestionTriggeredRef.current = true;
    setIsQuestionReading(true);
    console.log("📢 Step 1: Reading Question... (TTS playing)");
 
    if (!window.speechSynthesis) {
      console.warn("❌ Speech synthesis not supported.");
      setIsQuestionReading(false);
      isQuestionSpokenRef.current = true;
      startCountdown();
      return;
    }
 
    const speak = () => {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.2;
 
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice =
        voices.find(
          (v) =>
            v.lang === "en-IN" &&
            v.name.toLowerCase().includes("female")
        ) ||
        voices.find(
          (v) =>
            v.lang === "en-IN" &&
            v.name.toLowerCase().includes("google")
        ) ||
        voices.find((v) => v.lang === "en-IN") ||
        voices.find(
          (v) =>
            v.lang.startsWith("en") &&
            v.name.toLowerCase().includes("female")
        ) ||
        voices.find((v) => v.lang.startsWith("en"));
 
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
 
      utterance.onend = () => {
        setIsQuestionReading(false);
        isQuestionSpokenRef.current = true;
        startCountdown();
      };
 
      utterance.onerror = (e) => {
        console.error("❌ Speech synthesis error:", e);
        setIsQuestionReading(false);
        isQuestionSpokenRef.current = true;
        startCountdown();
      };
 
      window.speechSynthesis.speak(utterance);
    };
 
    if (speechSynthesis.getVoices().length === 0) {
      const loadVoices = () => {
        speechSynthesis.removeEventListener(
          "voiceschanged",
          loadVoices
        );
        speak();
      };
      speechSynthesis.addEventListener(
        "voiceschanged",
        loadVoices,
        { once: true }
      );
    } else {
      speak();
    }
  };
 
  // ========== TEXT-TO-SPEECH: Recording Started ==========
  const speakRecordingStarted = () => {
    const microphoneTracks = audioStream?.getAudioTracks?.() || [];
    // Suspend the source while system TTS plays. Sarvam consequently never
    // receives the prompt or its acoustic echo.
    microphoneTracks.forEach((track) => {
      track.enabled = false;
    });

    const activateCandidateTranscription = () => {
      // Do not capture the announcement itself or its speaker echo.
      transcriptActivationTimeoutRef.current = setTimeout(() => {
        microphoneTracks.forEach((track) => {
          track.enabled = true;
        });
        setIsTranscriptCaptureActive(true);
        transcriptActivationTimeoutRef.current = null;
      }, 2000);
    };

    setIsTranscriptCaptureActive(false);
    console.log("🔊 Step 6: 'Recording started. Please speak now.' announced (TTS)");
   
    if (!window.speechSynthesis) {
      activateCandidateTranscription();
      console.warn("❌ Speech synthesis not supported for recording announcement");
      return;
    }
 
    const utterance = new SpeechSynthesisUtterance("Recording started. Please speak now.");
    utterance.rate = 0.9;
    utterance.pitch = 1.2;
 
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find(v => v.lang === "en-IN") || voices.find(v => v.lang.startsWith("en"));
    if (voice) {
      utterance.voice = voice;
    }
 
    utterance.onend = () => {
      activateCandidateTranscription();
      console.log("🔊 Recording announcement finished");
    };
 
    utterance.onerror = (e) => {
      activateCandidateTranscription();
      console.error("❌ Recording announcement error:", e);
    };
 
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };
 
  // ========== TRIGGER QUESTION SPEECH ==========
  useEffect(() => {
    if (!question?.QuestionDescription) {
      return;
    }
 
    const currentQuestionText = question.QuestionDescription;
    if (questionTextRef.current !== currentQuestionText) {
      questionTextRef.current = currentQuestionText;
      isQuestionTriggeredRef.current = false;
      isQuestionSpokenRef.current = false;
      countdownStartedRef.current = false;
      isRetrying.current = false;
      setRetrying(false);
    }
 
    if (isQuestionTriggeredRef.current) {
      return;
    }
 
    const timer = setTimeout(() => {
      speakQuestion(question.QuestionDescription);
    }, 500);
 
    return () => clearTimeout(timer);
  }, [currentQuestion, question]);
 
  // ========== MICROPHONE LEVEL ==========
  useEffect(() => {
    let audioContext, analyser, dataArray, source, rafId;
 
    if (isRecording && audioStream && audioStream.getAudioTracks().length) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      dataArray = new Uint8Array(analyser.frequencyBinCount);
      source = audioContext.createMediaStreamSource(audioStream);
      source.connect(analyser);
 
      const updateMic = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const level = Math.min(100, Math.round((avg / 255) * 100));
        setMicLevel(level);
        rafId = requestAnimationFrame(updateMic);
      };
     
      updateMic();
    } else {
      setMicLevel(0);
    }
 
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (source) source.disconnect();
      if (audioContext) audioContext.close();
      setMicLevel(0);
    };
  }, [isRecording, audioStream]);
 
  // ========== VIDEO STREAM ==========
  useEffect(() => {
    const video = document.getElementById(`popup-cam-${currentQuestion}`);
    if (video) {
      if (stream && isRecording && stream.getVideoTracks().length) {
        video.srcObject = stream;
      } else {
        video.srcObject = null;
      }
    }
 
    return () => {
      if (video) video.srcObject = null;
    };
  }, [stream, isRecording, currentQuestion]);
 
  // ========== CLEANUP ON UNMOUNT ==========
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
      if (recognitionRestartTimeout.current) {
        clearTimeout(recognitionRestartTimeout.current);
      }
    };
  }, []);
 
  // ========== SAVE HANDLER ==========
  const handleSaveClick = async () => {
    console.log("💾 Step 9: Save button clicked");
   
    stoppedByUser.current = true;
    isRetrying.current = false;
    setRetrying(false);
   
    if (recognitionRestartTimeout.current) {
      clearTimeout(recognitionRestartTimeout.current);
      recognitionRestartTimeout.current = null;
    }
   
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        console.log(err);
      }
    }
 
    onClose();
    onSaveStart();
 
    try {
      await handleSave(transcript);
      console.log("✅ Saved successfully");
    } catch (err) {
      alert("Failed to save. Please try again.");
    } finally {
      onSaveEnd();
    }
  };
 
  return (
    <Box
      className="modal-content"
      sx={{
        width: "100%",
        maxWidth: "640px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Status Indicator */}
      <Box sx={{ width: "100%", mt: 1 }}>
        {transcriptionError && (
          <Typography sx={{ fontSize: 13, mb: 1, color: "#c53030" }}>
            Live transcript: {transcriptionError}
          </Typography>
        )}
        {isQuestionReading ? (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#444",
            }}
          >
            📢 Reading Question...
          </Typography>
        ) : isCountdownActive ? (
          <>
            <Typography sx={{ fontSize: 14, mb: 0.5 }}>
              ⏱️ Recording starts in: {timer}s
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 10,
                background: "#e2e8f0",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "linear-gradient(135deg, #3182ce, #63b3ed)",
                  transition: "width 1s linear",
                }}
              />
            </Box>
          </>
        ) : timerFinished ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "4px 0",
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "red",
                animation: "blink 1s infinite",
              }}
            />
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#444",
              }}
            >
              🔴 Recording started — speak now
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#444",
            }}
          >
            {isQuestionSpokenRef.current ? "⏳ Preparing..." : "📖 Preparing question..."}
          </Typography>
        )}
      </Box>
 
      {/* Question text */}
      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
        {question?.QuestionDescription}
      </Typography>
 
      {/* Video */}
      <Box
        className="video"
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        <video
          id={`popup-cam-${currentQuestion}`}
          playsInline
          autoPlay
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
 
      {/* Mic Level */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 14 }}>Mic level</Typography>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              width: "100%",
              height: 20,
              background: "#edf2f7",
              borderRadius: 1,
              border: "1px solid #ccc",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${micLevel}%`,
                height: "100%",
                background: "linear-gradient(135deg, #2f855a, #68d391)",
                transition: "width 0.05s",
              }}
            />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 14, minWidth: 30 }}>{micLevel}</Typography>
      </Box>
 
      {/* Transcript */}
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            mb: 1,
            color: "#444",
          }}
        >
          Transcript
        </Typography>
        <textarea
          value={transcript}
          readOnly
          style={{
            width: "100%",
            minHeight: "120px",
            maxHeight: "180px",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #cbd5e0",
            resize: "none",
            overflowY: "auto",
            backgroundColor: "#f7fafc",
            color: "#2d3748",
            fontSize: "14px",
            lineHeight: "1.5",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </Box>
 
      {/* Footer buttons */}
      <Box
        className="modal-footer"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Button
          onClick={handleRetry}
          disabled={!timerFinished || retrying}
          variant="contained"
          sx={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            mr: 1,
            "&:hover": {
              backgroundColor: "#b71c1c",
            },
            "&:disabled": {
              backgroundColor: "#e0e0e0",
              color: "#999",
            },
          }}
        >
          {retrying ? "Retrying..." : "Retry"}
        </Button>
        <Button
          className="save-modal-button"
          onClick={handleSaveClick}
          disabled={!isRecording || !timerFinished || retrying}
          sx={{
            background: "linear-gradient(135deg, #f79033, #f05a6e)",
            color: "#fff",
            "&:hover": {
              opacity: 0.95,
            },
            "&:disabled": {
              background: "#e0e0e0",
              color: "#999",
            },
          }}
        >
          {timerFinished ? "💾 Save" : `⏳ Wait (${timer}s)`}
        </Button>
      </Box>
    </Box>
  );
};